import { useEffect, useRef, useState } from 'react';
import { Camera, Upload, AlertTriangle, ExternalLink, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase, uploadFile } from '../../lib/supabase';
import { useOwner } from '../../hooks/useOwner';
import { Modal } from './Modal';

// One expense form for the whole app (Expenses page and each vehicle's Expenses tab).
//
//  * Scan an invoice: take a photo with the phone / tablet camera (or choose a photo or
//    PDF). The scan-document function reads it and SUGGESTS the supplier, date, total,
//    category and tags. Everything lands in the form for the person to check; nothing is
//    saved until they press Save.
//  * Tags + supplier: every expense can carry tags ("tyres", "brake pads"), and the supplier
//    box suggests names already used, so spend can later be totalled per vendor or per tag.
//  * Awaiting payment: an invoice can be recorded as unpaid. The account owner then gets an
//    email and an alert in the app, with the invoice, the amount and the pay link if one was
//    added, and marks it paid once settled.

export const EXPENSE_CATEGORIES = ['Fuel', 'Service', 'Repairs', 'Insurance', 'Road Tax', 'Tyres', 'Cleaning', 'Parking', 'Fines', 'Parts', 'Restoration', 'Other'];
export const PAYMENT_METHODS = ['Cash', 'Card', 'Bank Transfer', 'Other'];
const QUICK_TAGS = ['tyres', 'brakes', 'oil change', 'battery', 'labour', 'parts', 'paint', 'electrical', 'detailing', 'parking', 'fuel'];
const MAX_MB = 10;

export const cleanTag = (t) => String(t || '').toLowerCase().replace(/\s+/g, ' ').replace(/[^a-z0-9Ͱ-Ͽ \-]/g, '').trim().slice(0, 24);
const normReg = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export const ExpenseModal = ({ vehicles: vehiclesProp = null, vehicleId = '', onClose, record = null, defaultCategory = null }) => {
  const { isOwner } = useOwner();
  const [vehicles, setVehicles] = useState(vehiclesProp || []);
  const [knownSuppliers, setKnownSuppliers] = useState([]);
  const [knownTags, setKnownTags] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: record?.vehicle_id || vehicleId || '',
    expense_date: record?.expense_date || '',
    category: record?.category || defaultCategory || 'Fuel',
    description: record?.description || '',
    amount: record?.amount ?? '',
    supplier: record?.supplier || '',
    payment_method: record?.payment_method || 'Card',
    receipt_url: record?.receipt_url || '',
    invoice_number: record?.invoice_number || '',
    notes: record?.notes || '',
    payment_status: record?.payment_status || 'paid',
    payment_link: record?.payment_link || '',
    due_date: record?.due_date || '',
  });
  const [tags, setTags] = useState(record?.tags || []);
  const [tagDraft, setTagDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);          // { reg, confidence, error }
  const [statusTouched, setStatusTouched] = useState(!!record);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const locked = !!vehicleId && !record;

  // Vehicles for the picker (or just this one, to check a scanned plate against it).
  useEffect(() => {
    if (vehiclesProp) { setVehicles(vehiclesProp); return; }
    if (!vehicleId) return;
    supabase.from('vehicles').select('id, registration_number, brand, model, user_id').eq('id', vehicleId)
      .then(({ data }) => setVehicles(data || []));
  }, [vehiclesProp, vehicleId]);

  // Suppliers and tags already used, for suggestions — keeps "Kyriacos Tyres" from
  // turning into three slightly different spellings.
  useEffect(() => {
    supabase.from('expenses').select('supplier, tags').limit(5000).then(({ data }) => {
      const sup = new Map(); const tg = new Map();
      (data || []).forEach(r => {
        const s = (r.supplier || '').trim();
        if (s) sup.set(s.toLowerCase(), (sup.get(s.toLowerCase()) || [s, 0]).map((x, i) => (i === 1 ? x + 1 : x)));
        (r.tags || []).forEach(t => tg.set(t, (tg.get(t) || 0) + 1));
      });
      setKnownSuppliers([...sup.values()].sort((a, b) => b[1] - a[1]).map(x => x[0]).slice(0, 100));
      setKnownTags([...tg.entries()].sort((a, b) => b[1] - a[1]).map(x => x[0]).slice(0, 60));
    });
  }, []);

  const addTag = (raw) => {
    const t = cleanTag(raw);
    if (!t) return;
    setTags(prev => (prev.includes(t) || prev.length >= 10 ? prev : [...prev, t]));
    setTagDraft('');
  };
  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));

  const selected = vehicles.find(v => v.id === form.vehicle_id);

  // ── Scan an invoice ───────────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`That file is too large (${MAX_MB} MB maximum)`); return; }
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf && !file.type.startsWith('image/')) { toast.error('Please choose a photo (JPG, PNG) or a PDF'); return; }

    setScanning(true);
    setScanInfo(null);
    let up;
    try {
      up = await uploadFile(file, 'receipts');
      set('receipt_url', up.url);
    } catch (e) {
      toast.error('Upload failed: ' + e.message);
      setScanning(false);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('scan-document', { body: { fileUrl: up.url, mode: 'expense' } });
      if (error || data?.error || !data?.suggestion) throw new Error(data?.error || error?.message || 'Could not read the invoice');
      const s = data.suggestion;
      const f = s.fields || {};
      setForm(prev => ({
        ...prev,
        supplier: f.supplier ?? prev.supplier,
        expense_date: f.expense_date ?? prev.expense_date,
        amount: f.amount ?? prev.amount,
        description: f.description ?? prev.description,
        category: f.category ?? prev.category,
        invoice_number: f.invoice_number ?? prev.invoice_number,
      }));
      if (Array.isArray(s.tags)) setTags(prev => [...new Set([...prev, ...s.tags.map(cleanTag).filter(Boolean)])].slice(0, 10));
      setScanInfo({ reg: s.registration_number || '', confidence: s.confidence ?? 0 });
    } catch (e) {
      setScanInfo({ error: e.message || 'Could not read the invoice' });
    }
    // A team member photographing an invoice is normally handing it to the owner to pay.
    if (!statusTouched && !isOwner) set('payment_status', 'unpaid');
    setScanning(false);
  };

  const docReg = normReg(scanInfo?.reg);
  const mismatch = docReg && selected && normReg(selected.registration_number) !== docReg;
  const matching = mismatch ? vehicles.find(v => normReg(v.registration_number) === docReg) : null;

  // ── Save ──────────────────────────────────────────────────────────────────────
  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error('Please select a vehicle'); return; }
    if (!form.expense_date) { toast.error('Please enter a date'); return; }
    if (form.amount === '' || form.amount === null || isNaN(parseFloat(form.amount))) { toast.error('Please enter an amount'); return; }
    const unpaid = form.payment_status === 'unpaid';
    let link = (form.payment_link || '').trim();
    if (unpaid && link) {
      if (!/^https?:\/\//i.test(link)) link = 'https://' + link;
      try { new URL(link); } catch { toast.error('That payment link doesn\'t look right'); return; }
    }

    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    // Expenses belong to the account owner (so the owner always sees them, whoever typed them in).
    let ownerId = selected?.user_id;
    if (!ownerId) {
      const { data: v } = await supabase.from('vehicles').select('user_id').eq('id', form.vehicle_id).maybeSingle();
      ownerId = v?.user_id;
    }
    const wasUnpaid = record?.payment_status === 'unpaid';
    const payload = {
      vehicle_id: form.vehicle_id,
      user_id: ownerId || user.id,
      expense_date: form.expense_date,
      category: form.category,
      description: form.description || null,
      amount: parseFloat(form.amount),
      supplier: form.supplier.trim() || null,
      payment_method: form.payment_method || null,
      receipt_url: form.receipt_url || null,
      invoice_number: form.invoice_number.trim() || null,
      notes: form.notes || null,
      tags,
      payment_status: form.payment_status,
      payment_link: unpaid ? (link || null) : null,
      due_date: unpaid ? (form.due_date || null) : null,
      paid_at: unpaid ? null : (record?.paid_at || null),
    };
    if (!record) payload.created_by = user.id;

    let saved, error;
    if (record) {
      ({ data: saved, error } = await supabase.from('expenses').update(payload).eq('id', record.id).select().single());
    } else {
      ({ data: saved, error } = await supabase.from('expenses').insert(payload).select().single());
    }
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }

    // Tell the owner about a new unpaid invoice (or an existing expense just switched to unpaid).
    if (unpaid && saved?.id && (!record || !wasUnpaid)) {
      try {
        const { data: res, error: fnErr } = await supabase.functions.invoke('notify-invoice', { body: { expenseId: saved.id } });
        if (fnErr || res?.error) throw new Error(res?.error || fnErr?.message);
        toast.success(res?.emailed ? 'Saved. The owner has been emailed.' : 'Saved. It will show as an alert for the owner.');
      } catch {
        toast.success('Saved. It will show as an alert for the owner.');
        toast('The email could not be sent right now.', { icon: 'ℹ️' });
      }
    } else {
      toast.success(record ? 'Expense updated!' : 'Expense added!');
    }
    onClose();
    setSaving(false);
  };

  const banner = (tone, children) => (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.5,
      background: tone === 'warn' ? 'rgba(249,115,22,0.10)' : 'var(--surface2)',
      border: `1px solid ${tone === 'warn' ? 'rgba(249,115,22,0.4)' : 'var(--border)'}`,
    }}>
      {tone === 'warn' && <AlertTriangle size={16} style={{ color: '#f97316', flexShrink: 0, marginTop: 2 }} />}
      <div>{children}</div>
    </div>
  );

  const isImageUrl = /\.(jpe?g|png|webp|gif)(\?|$)/i.test(form.receipt_url || '');

  return (
    <Modal
      title={record ? 'Edit Expense' : 'Add Expense'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="expense-form" type="submit" disabled={saving || scanning}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="expense-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Scan an invoice */}
        <div style={{ border: '1px dashed var(--border)', borderRadius: 12, padding: 14, background: 'var(--surface2)' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            {form.receipt_url && isImageUrl && (
              <a href={form.receipt_url} target="_blank" rel="noreferrer">
                <img src={form.receipt_url} alt="Invoice" style={{ width: 64, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
              </a>
            )}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                {form.receipt_url ? 'Invoice attached' : 'Add by photo'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                {scanning ? 'Reading the invoice…' : 'Take a photo of the invoice and we\'ll fill the form in for you to check.'}
              </div>
              {form.receipt_url && !isImageUrl && (
                <a href={form.receipt_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <ExternalLink size={11} /> View file
                </a>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary btn-sm" disabled={scanning} onClick={() => cameraRef.current?.click()}>
                <Camera size={13} /> Take photo
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={scanning} onClick={() => fileRef.current?.click()}>
                <Upload size={13} /> Choose file
              </button>
            </div>
          </div>
          {scanning && <div className="spinner" style={{ margin: '12px auto 0' }} />}
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
        </div>

        {scanInfo?.error && banner('warn', <>We couldn't read this automatically ({scanInfo.error}). The file is attached, so you can fill in the details by hand.</>)}
        {scanInfo && !scanInfo.error && scanInfo.confidence < 60 && banner('warn', <>We're not very sure about this one. Please check every field before saving.</>)}
        {mismatch && banner('warn', (
          <>
            This invoice shows registration <strong>{scanInfo.reg}</strong>, but the expense is for <strong>{selected.registration_number}</strong>.
            {matching && !locked && (
              <> <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => set('vehicle_id', matching.id)}>Switch to {matching.registration_number}</button></>
            )}
            {!matching && <> Make sure it belongs to this car before saving.</>}
          </>
        ))}

        {!locked && (
          <div className="form-group">
            <label className="form-label">Vehicle *</label>
            <select className="form-input" required value={form.vehicle_id} onChange={e => set('vehicle_id', e.target.value)}>
              <option value="">Select vehicle…</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number} — {v.brand} {v.model}</option>)}
            </select>
          </div>
        )}

        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Date *</label>
            <input className="form-input" type="date" required value={form.expense_date} onChange={e => set('expense_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Category</label>
            <select className="form-input" value={form.category} onChange={e => set('category', e.target.value)}>
              {EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Amount (€) *</label>
            <input className="form-input" type="number" step="0.01" min="0" required value={form.amount} onChange={e => set('amount', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select className="form-input" value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
              {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Supplier / Vendor</label>
            <input className="form-input" list="expense-suppliers" value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder="e.g. Kyriacos Tyres" />
            <datalist id="expense-suppliers">{knownSuppliers.map(s => <option key={s} value={s} />)}</datalist>
          </div>
          <div className="form-group">
            <label className="form-label">Invoice number</label>
            <input className="form-input" value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>

        {/* Tags */}
        <div className="form-group">
          <label className="form-label">Tags <span style={{ color: 'var(--text3)', fontWeight: 400 }}>— so you can total spend later (e.g. all tyres)</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
            {tags.map(t => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'rgba(79,142,247,0.14)', color: 'var(--accent)' }}>
                {t}
                <button type="button" onClick={() => removeTag(t)} aria-label={`Remove ${t}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={11} /></button>
              </span>
            ))}
            <input
              list="expense-tags"
              value={tagDraft}
              placeholder={tags.length ? 'Add another…' : 'Type a tag and press Enter'}
              onChange={e => setTagDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagDraft); }
                if (e.key === 'Backspace' && !tagDraft && tags.length) removeTag(tags[tags.length - 1]);
              }}
              onBlur={() => addTag(tagDraft)}
              style={{ flex: 1, minWidth: 120, border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontSize: 13, padding: '4px 2px' }}
            />
            <datalist id="expense-tags">{[...new Set([...knownTags, ...QUICK_TAGS])].map(t => <option key={t} value={t} />)}</datalist>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {[...new Set([...knownTags.slice(0, 6), ...QUICK_TAGS])].filter(t => !tags.includes(t)).slice(0, 10).map(t => (
              <button key={t} type="button" onClick={() => addTag(t)} style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text3)' }}>+ {t}</button>
            ))}
          </div>
        </div>

        {/* Payment status */}
        <div className="form-group">
          <label className="form-label">Payment</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['paid', 'Already paid'], ['unpaid', 'Awaiting payment']].map(([val, label]) => (
              <button key={val} type="button"
                onClick={() => { set('payment_status', val); setStatusTouched(true); }}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${form.payment_status === val ? 'var(--accent)' : 'var(--border)'}`,
                  background: form.payment_status === val ? 'rgba(79,142,247,0.14)' : 'var(--surface)',
                  color: form.payment_status === val ? 'var(--accent)' : 'var(--text2)',
                }}>{label}</button>
            ))}
          </div>
          {form.payment_status === 'unpaid' && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
                The owner gets an email and an alert in the app with this invoice. They can pay it with the link below
                (if you add one) and then mark it as paid.
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Payment link (optional)</label>
                  <input className="form-input" type="url" inputMode="url" placeholder="https://…" value={form.payment_link} onChange={e => set('payment_link', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pay by (optional)</label>
                  <input className="form-input" type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};
